/**
 * Toast — Notification éphémère (succès / erreur / info).
 *
 * Composant minimal stateless : monté quand `isOpen=true`, démonté sinon.
 * Auto-fermeture après `duration` ms (défaut 4000), appel `onClose()` à
 * l'expiration. Pas de queue, un seul toast à la fois (premier appel gagne).
 *
 * Accessibilité :
 *   - role="status" pour succès/info (lecture polite).
 *   - role="alert" pour erreur (lecture assertive).
 *   - aria-live cohérent.
 *   - Bouton de fermeture explicite (aria-label).
 *
 * Style : tokens theme.js (BRAND.green/red, RADIUS.md, SHADOW.elevated).
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { BRAND, RADIUS, SHADOW, TYPE } from '../constants/theme';

const VARIANTS = {
  success: { bg: BRAND.green,  ariaRole: 'status', ariaLive: 'polite'    },
  error:   { bg: BRAND.red,    ariaRole: 'alert',  ariaLive: 'assertive' },
  info:    { bg: BRAND.blue,   ariaRole: 'status', ariaLive: 'polite'    },
};

export default function Toast({ isOpen, type = 'info', message, onClose, duration = 4000 }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => onClose && onClose(), duration);
    return () => clearTimeout(timer);
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const variant = VARIANTS[type] || VARIANTS.info;

  return (
    <div
      role={variant.ariaRole}
      aria-live={variant.ariaLive}
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        background: variant.bg,
        color: '#FFFFFF',
        padding: '14px 18px',
        borderRadius: RADIUS.md,
        boxShadow: SHADOW.elevated,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        maxWidth: 480,
        zIndex: 11000,
        ...TYPE.body,
        fontWeight: 500,
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('app.toast.close')}
        style={{
          background: 'transparent',
          color: '#FFFFFF',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
